import type { ReactNode, JSX } from 'react'
import { createContext, useContext, useMemo, useRef, forwardRef } from 'react'

import { clsx } from 'clsx'

import type {
  ChipsMultipleProps,
  ComboboxProps,
  ComboboxTriggerProps,
  ComboboxItemProps,
  ComboboxAnchorProps,
  ComboboxContentProps,
} from '@buffer-mono/popcorn'
import {
  Badge,
  Button,
  ChannelAvatar,
  Checkbox,
  Chips,
  Combobox,
  EmptyState,
  EyeIcon,
  PlusIcon,
  SearchIcon,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { useVisibleElements } from '~publish/hooks/useVisibleElements'
import { isChannelType } from '~publish/helpers/channels'

import { GroupTooltipContent } from './GroupTooltipContent'
import type { ChannelGroup, NormalizedChannel } from './types'

import styles from './ChannelSelector.module.css'

// Internal context — provides selected channel IDs to child components
interface ChannelSelectorContextValue {
  selectedChannelIds: string[]
}

const ChannelSelectorContext = createContext<
  ChannelSelectorContextValue | undefined
>(undefined)

type TriggerProps = ComboboxTriggerProps

function Trigger({ children, ...triggerProps }: TriggerProps): JSX.Element {
  return <Combobox.Trigger {...triggerProps}>{children}</Combobox.Trigger>
}

type AnchorProps = ComboboxAnchorProps

function Anchor({ children }: AnchorProps): JSX.Element {
  return <Combobox.Anchor>{children}</Combobox.Anchor>
}

function Content({
  children,
  className,
  side = 'bottom',
  align = 'start',
  ...props
}: Omit<ComboboxContentProps, 'side' | 'align'> & {
  side?: ComboboxContentProps['side']
  align?: ComboboxContentProps['align']
}): JSX.Element {
  return (
    <Combobox.Content
      className={className ?? styles.content}
      side={side}
      align={align}
      {...props}
    >
      {children}
    </Combobox.Content>
  )
}

function Search({
  placeholder = 'Search channels',
  ...props
}: React.ComponentPropsWithoutRef<typeof Combobox.Input>): JSX.Element {
  return (
    <Combobox.Input
      aria-label="Search channels by name or location"
      placeholder={placeholder}
      prefix={<SearchIcon />}
      {...props}
    />
  )
}

function List({ children }: { children: ReactNode }): JSX.Element {
  return <Combobox.List>{children}</Combobox.List>
}

function Empty({ children }: { children?: ReactNode }): JSX.Element {
  const defaultEmpty = (
    <EmptyState size="small">
      <EmptyState.Icon>
        <SearchIcon />
      </EmptyState.Icon>
      <EmptyState.Description>No channels found</EmptyState.Description>
    </EmptyState>
  )

  return <Combobox.Empty>{children ?? defaultEmpty}</Combobox.Empty>
}

interface ItemProps extends ComboboxItemProps {
  channel: NormalizedChannel
  className?: string
  /**
   * Explanation shown in a tooltip when the item is disabled — e.g. why the
   * channel can't be selected. Ignored unless the item is effectively disabled.
   */
  disabledReason?: ReactNode
  /** Renders a view-only eye icon next to the channel name. */
  viewOnly?: boolean
  /** Tooltip explaining the view-only access; on a disabled item `disabledReason` wins. */
  viewOnlyReason?: ReactNode
}

function Item({
  channel,
  className,
  disabledReason,
  viewOnly = false,
  viewOnlyReason,
  ...props
}: ItemProps): JSX.Element {
  const context = useContext(ChannelSelectorContext)
  const isSelected = context?.selectedChannelIds.includes(channel.id) ?? false
  const { name, service, avatar, locationData, serverUrl } = channel
  const subtitle = locationData || serverUrl || ''
  // Already-selected channels stay unselectable even when `disabled`, so the
  // "disabled" visuals (faded avatar, tooltip) apply only to the ones the user
  // truly can't act on.
  const isDisabled = Boolean(props.disabled) && !isSelected
  const tooltip = isDisabled
    ? disabledReason
    : viewOnly
      ? viewOnlyReason
      : undefined

  const channelDetails = (
    <div className={styles.channel}>
      {isChannelType(service) && (
        <ChannelAvatar
          size="small"
          alt=""
          src={avatar ?? undefined}
          name={name}
          channel={service}
          className={clsx(styles.avatar, isDisabled && styles.avatarDisabled)}
        />
      )}
      <span className={styles.nameCell}>
        <Text
          weight="medium"
          color={isDisabled ? 'subtle' : 'default'}
          className={styles.name}
          translate="no"
        >
          {name}
          <VisuallyHidden>({service})</VisuallyHidden>
        </Text>
        {viewOnly && (
          <>
            <EyeIcon
              size="xsmall"
              color="subtle"
              className={styles.viewOnlyIcon}
              aria-hidden
            />
            <VisuallyHidden>(view only)</VisuallyHidden>
          </>
        )}
      </span>
      {subtitle && (
        <Text
          color={isDisabled ? 'disabled' : 'subtle'}
          className={styles.subtitle}
          translate="no"
        >
          {subtitle}
        </Text>
      )}
    </div>
  )

  return (
    <Combobox.Item
      customSelectedStatus
      className={clsx(styles.item, className)}
      {...props}
    >
      {/* The tooltip wraps the row content (not the Combobox.Item) because the
          item owns its own pointer handlers, which would override the ones the
          tooltip trigger needs. */}
      {tooltip ? (
        <Tooltip content={tooltip}>{channelDetails}</Tooltip>
      ) : (
        channelDetails
      )}
      <Checkbox
        checked={isSelected}
        tabIndex={-1}
        className={styles.checkbox}
        disabled={isDisabled}
      />
    </Combobox.Item>
  )
}

function ChannelGroupItem({
  channelGroup,
  isSelected: isSelectedProp,
  onClick,
  disabled = false,
}: {
  channelGroup: ChannelGroup
  isSelected?: boolean
  onClick?: () => void
  disabled?: boolean
}): JSX.Element {
  const context = useContext(ChannelSelectorContext)
  const channelIds = channelGroup.channels?.map((channel) => channel.id) ?? []
  const isSelectedFromContext = context
    ? channelIds.length > 0 &&
      channelIds.every((id) => context.selectedChannelIds.includes(id))
    : false
  const isSelected = isSelectedProp ?? isSelectedFromContext

  return (
    <Tooltip
      align="start"
      content={
        <GroupTooltipContent
          name={channelGroup.name}
          channels={channelGroup.channels ?? []}
        />
      }
    >
      <Chips.Item
        value={String(channelGroup.id)}
        className={clsx(styles.channelGroupItem, styles.channelGroupChip)}
        onClick={onClick}
        disabled={disabled}
        data-state={isSelected ? 'on' : 'off'}
      >
        <Text className={styles.groupName} translate="no">
          {channelGroup.name}
        </Text>{' '}
        <Badge className={styles.count}>
          {channelGroup.channels?.length}
          <VisuallyHidden>
            {channelGroup.channels?.length === 1 ? 'channel' : 'channels'} in
            this group
          </VisuallyHidden>
        </Badge>
        <VisuallyHidden>
          {disabled
            ? 'No channels in this group can be selected'
            : isSelected
              ? 'Click to unselect all channels in this group'
              : 'Click to select all channels in this group'}
        </VisuallyHidden>
      </Chips.Item>
    </Tooltip>
  )
}

function ChannelGroupLink({
  href = '/settings/channel-groups',
  children = 'Manage groups',
  onClick,
}: {
  href?: string
  children?: ReactNode
  onClick?: () => void
}): JSX.Element {
  return (
    <Button
      as="a"
      href={href}
      target="_blank"
      rel="noopener"
      color="inherit"
      variant="secondary"
      data-state="off"
      className={styles.channelGroupItem}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

export interface ChannelGroupsProps extends Omit<
  ChipsMultipleProps,
  'value' | 'type'
> {
  value: string[]
  className?: string
}

const ChannelGroups = forwardRef<HTMLDivElement, ChannelGroupsProps>(
  function ChannelGroups({ children, className, ...props }, ref) {
    return (
      <Chips
        ref={ref}
        type="multiple"
        className={clsx(styles.channelGroupsContainer, className)}
        {...props}
      >
        {children}
      </Chips>
    )
  },
)

interface GroupsOverflowProps {
  groups: ChannelGroup[]
  selectedIds: string[]
  /** Group IDs to render disabled (e.g. every channel in them is unselectable). */
  disabledIds?: string[]
  onSelectGroup: (channelIds: string[], isSelected: boolean) => void
  isExpanded: boolean
  onExpand: () => void
  /** Slot for the trailing action (e.g. NewGroupLink) shown when expanded or no overflow */
  children?: ReactNode
}

function GroupsOverflow({
  groups,
  selectedIds,
  disabledIds,
  onSelectGroup,
  isExpanded,
  onExpand,
  children,
}: GroupsOverflowProps): JSX.Element {
  const overflowRef = useRef<HTMLButtonElement>(null)
  const { containerRef, visibleCount, hiddenCount } = useVisibleElements({
    totalCount: groups.length,
    minimumVisibleCount: 1,
    direction: 'vertical',
    maxRows: 2,
    overflowRef,
  })

  const visibleGroups = useMemo(
    () => (isExpanded ? groups : groups.slice(0, visibleCount)),
    [isExpanded, groups, visibleCount],
  )
  const hiddenGroups = useMemo(
    () => (hiddenCount > 0 ? groups.slice(visibleCount) : []),
    [groups, hiddenCount, visibleCount],
  )

  const hasHiddenGroupsSelected = useMemo(
    () => hiddenGroups.some((group) => selectedIds.includes(String(group.id))),
    [hiddenGroups, selectedIds],
  )

  return (
    <div>
      <VisuallyHidden id="channel-groups-description">
        Select one of your channel groups to quick select the channels in it to
        post to.{' '}
      </VisuallyHidden>

      <ChannelGroups
        ref={containerRef}
        value={selectedIds}
        aria-describedby="channel-groups-description"
        className={styles.groupsOverflowContainer}
      >
        {visibleGroups.map((group) => (
          <ChannelGroupItem
            key={`channel-group-${group.id}`}
            channelGroup={group}
            isSelected={selectedIds.includes(String(group.id))}
            disabled={disabledIds?.includes(String(group.id)) ?? false}
            onClick={() => {
              const channelIds = group.channels?.map((c) => c.id) ?? []
              const isSelected = selectedIds.includes(String(group.id))
              onSelectGroup(channelIds, isSelected)
            }}
          />
        ))}

        {hiddenGroups.length > 0 && !isExpanded ? (
          <Button
            ref={overflowRef}
            variant="secondary"
            className={clsx(
              styles.remainingCount,
              hasHiddenGroupsSelected && styles.selected,
            )}
            onClick={onExpand}
          >
            <PlusIcon />
            {hiddenGroups.length}
          </Button>
        ) : (
          children
        )}
      </ChannelGroups>
    </div>
  )
}

const Group = Combobox.Group

export interface ChannelSelectorRootProps {
  value: string[]
  onChange: (channelIds: string[]) => void
  children: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultOpen?: boolean
  modal?: boolean
  filter?: ComboboxProps['filter']
}

function Root({
  value,
  onChange,
  children,
  open,
  onOpenChange,
  defaultOpen,
  modal,
  filter,
}: ChannelSelectorRootProps): JSX.Element {
  const contextValue = useMemo(() => ({ selectedChannelIds: value }), [value])

  return (
    <ChannelSelectorContext.Provider value={contextValue}>
      <Combobox
        multiple
        value={value}
        onChange={onChange}
        open={open}
        onOpenChange={onOpenChange}
        defaultOpen={defaultOpen}
        modal={modal}
        filter={filter}
      >
        {children}
      </Combobox>
    </ChannelSelectorContext.Provider>
  )
}

export const ChannelSelector = Object.assign(Root, {
  Trigger,
  Anchor,
  Content,
  Search,
  Groups: ChannelGroups,
  GroupItem: ChannelGroupItem,
  GroupsOverflow,
  NewGroupLink: ChannelGroupLink,
  Group,
  List,
  Item,
  Empty,
})
